import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, AlertTriangle, Wallet, Hammer, PackageCheck, Inbox, Check, X, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const POLL_MS = 30000;

const EVENT_META = {
  'jobOrder.problem':        { icon: AlertTriangle, color: '#ef4444' },
  'jobOrder.assigned':       { icon: Hammer,        color: '#9333ea' },
  'jobOrder.new':            { icon: Inbox,         color: '#0ea5e9' },
  'proposal.assigned':       { icon: Inbox,         color: '#0ea5e9' },
  'procurement.assigned':    { icon: Hammer,        color: '#9333ea' },
  'paymentRequest.created':  { icon: Inbox,         color: '#0ea5e9' },
  'paymentRequest.message':  { icon: MessageCircle, color: '#0ea5e9' },
  'paymentRequest.approved': { icon: Check,         color: '#10b981' },
  'paymentRequest.rejected': { icon: AlertTriangle, color: '#ef4444' },
  'payment.incoming':        { icon: Wallet,        color: '#14b8a6' },
  'receipt.verified':        { icon: PackageCheck,  color: '#22c55e' },
  'receipt.status':          { icon: PackageCheck,  color: '#f59e0b' },
};

// onNavigate(key) — DashboardPage active-key sahifalariga o'tish uchun
export default function NotificationToastContainer({ onNavigate }) {
  const notifStyle  = useSelector(s => s.auth.user?.organization?.settings?.notificationStyle);
  const durationSec = useSelector(s => s.auth.user?.organization?.settings?.notificationToastDuration ?? 6);
  const isEnabled = notifStyle === 'toast';
  const isAuth = useSelector(s => s.auth.isAuthenticated);
  const navigate = useNavigate();

  const [toasts, setToasts] = useState([]);
  const lastIdRef = useRef(null);
  const initializedRef = useRef(false);

  const poll = useCallback(async () => {
    if (!isEnabled || !isAuth) return;
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: 5 } });
      const items = r.data.notifications || [];

      if (!initializedRef.current) {
        initializedRef.current = true;
        lastIdRef.current = items[0]?._id || null;
        return;
      }

      const newItems = [];
      for (const item of items) {
        if (String(item._id) === String(lastIdRef.current)) break;
        newItems.push(item);
      }

      if (newItems.length > 0) {
        lastIdRef.current = String(items[0]._id);
        setToasts(prev => [
          ...newItems.map(n => ({ ...n, toastId: n._id + '_' + Date.now() })),
          ...prev,
        ].slice(0, 4));
      }
    } catch { /* silent */ }
  }, [isEnabled, isAuth]);

  useEffect(() => {
    if (!isEnabled) { setToasts([]); initializedRef.current = false; return; }
    initializedRef.current = false;
    poll();
    const id = setInterval(poll, POLL_MS);
    window.addEventListener('erp:notif', poll);
    return () => { clearInterval(id); window.removeEventListener('erp:notif', poll); };
  }, [isEnabled, poll]);

  // Auto-dismiss: durationSec soniyadan so'ng eng eski toastni o'chiradi (0 = o'chmasin)
  useEffect(() => {
    if (toasts.length === 0 || durationSec <= 0) return;
    const id = setTimeout(() => {
      setToasts(prev => prev.slice(0, -1));
    }, durationSec * 1000);
    return () => clearTimeout(id);
  }, [toasts, durationSec]);

  const dismiss = (toastId) => setToasts(prev => prev.filter(t => t.toastId !== toastId));

  const go = (n) => {
    dismiss(n.toastId);
    axios.post(`${API}/notifications/${n._id}/read`).catch(() => {});
    const link = n.link || '';
    if (link.startsWith('dash:')) onNavigate?.(link.slice(5));
    else if (link.startsWith('/')) navigate(link);
  };

  if (!isEnabled || toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 items-end pointer-events-none"
      style={{ maxWidth: '340px', width: 'calc(100vw - 32px)' }}>
      {toasts.map(n => {
        const m = EVENT_META[n.event] || { icon: Bell, color: '#94a3b8' };
        const Icon = m.icon;
        return (
          <div key={n.toastId}
            className="notif-toast-enter pointer-events-auto w-full bg-surface rounded-xl shadow-modal border border-surface-100 p-3.5 flex items-start gap-3">
            <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
              style={{ backgroundColor: m.color + '20' }}>
              <Icon className="w-4 h-4" style={{ color: m.color }} />
            </span>
            <button className="flex-1 text-left min-w-0" onClick={() => go(n)}>
              <p className="text-sm font-semibold text-ink leading-snug">{n.title}</p>
              {n.body && <p className="text-xs text-ink-secondary mt-0.5 line-clamp-2">{n.body}</p>}
            </button>
            <button onClick={() => dismiss(n.toastId)}
              className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-ink-tertiary hover:text-ink hover:bg-surface-100 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
